import { OrderDetails, SeatRequirement, SeatUploadRequirements, SeatSlot } from '../types';

/**
 * @description Patterns for detecting seat types
 */
export const SEAT_TYPE_PATTERNS = {
  FIRST_ROW: ['first row', 'front seat'],
  SECOND_THIRD_ROW: ['second or third row', 'second row', 'third row', 'rear seat'],
  TWO_ROW_BUNDLE: ['2 row bundle'],
  THREE_ROW_BUNDLE: ['3 row bundle'],
};

/**
 * @description Configuration for seat slots based on coverage type
 */
export const SEAT_SLOT_CONFIG: Record<string, Omit<SeatSlot, 'id'>[]> = {
  FIRST_ROW: [
    { label: 'FRONT SEATS', imagePosition: 'first_row_1', isRequired: true },
  ],
  SECOND_THIRD_ROW: [
    { label: '2ND ROW ANGLE 1', imagePosition: 'second_row_1', isRequired: true },
    { label: '2ND ROW ANGLE 2', imagePosition: 'second_row_2', isRequired: true },
    { label: '2ND ROW ANGLE 3', imagePosition: 'second_row_3', isRequired: true },
  ],
  TWO_ROW_BUNDLE: [
    { label: 'FRONT SEATS', imagePosition: 'first_row_1', isRequired: true },
    { label: '2ND ROW ANGLE 1', imagePosition: 'second_row_1', isRequired: true },
    { label: '2ND ROW ANGLE 2', imagePosition: 'second_row_2', isRequired: true },
    { label: '2ND ROW ANGLE 3', imagePosition: 'second_row_3', isRequired: true },
  ],
  THREE_ROW_BUNDLE: [
    { label: 'FRONT SEATS', imagePosition: 'first_row_1', isRequired: true },
    { label: '2ND ROW ANGLE 1', imagePosition: 'second_row_1', isRequired: true },
    { label: '2ND ROW ANGLE 2', imagePosition: 'second_row_2', isRequired: true },
    { label: '2ND ROW ANGLE 3', imagePosition: 'second_row_3', isRequired: true },
    { label: '3RD ROW ANGLE 1', imagePosition: 'third_row_1', isRequired: true },
    { label: '3RD ROW ANGLE 2', imagePosition: 'third_row_2', isRequired: true },
  ],
};

/**
 * @description Detects the seat type from the custom attributes
 */
function detectSeatTypeFromAttributes(customAttributes: Array<{ key: string, value: string }>): string | null {
  for (const attr of customAttributes) {
    const key = attr.key.toLowerCase();
    const value = attr.value.toLowerCase();

    if (key.includes('seat') || key.includes('_seat') || key.includes('row')) {
      if (SEAT_TYPE_PATTERNS.THREE_ROW_BUNDLE.some(pattern => value.includes(pattern))) {
        return 'THREE_ROW_BUNDLE';
      }
      if (SEAT_TYPE_PATTERNS.TWO_ROW_BUNDLE.some(pattern => value.includes(pattern))) {
        return 'TWO_ROW_BUNDLE';
      }
      if (SEAT_TYPE_PATTERNS.FIRST_ROW.some(pattern => value.includes(pattern))) {
        return 'FIRST_ROW';
      }
      if (SEAT_TYPE_PATTERNS.SECOND_THIRD_ROW.some(pattern => value.includes(pattern))) {
        return 'SECOND_THIRD_ROW';
      }
    }
  }
  return null;
}

function detectSeatTypeFromTitle(title: string): string | null {
  const lowerTitle = title.toLowerCase();

  if (SEAT_TYPE_PATTERNS.THREE_ROW_BUNDLE.some(pattern => lowerTitle.includes(pattern))) {
    return 'THREE_ROW_BUNDLE';
  }
  if (SEAT_TYPE_PATTERNS.TWO_ROW_BUNDLE.some(pattern => lowerTitle.includes(pattern))) {
    return 'TWO_ROW_BUNDLE';
  }
  if (SEAT_TYPE_PATTERNS.FIRST_ROW.some(pattern => lowerTitle.includes(pattern))) {
    return 'FIRST_ROW';
  }
  if (SEAT_TYPE_PATTERNS.SECOND_THIRD_ROW.some(pattern => lowerTitle.includes(pattern))) {
    return 'SECOND_THIRD_ROW';
  }
  return null;
}

function detectSeatTypeFromTags(tags: string[]): string | null {
  for (const tag of tags) {
    const lowerTag = tag.toLowerCase();

    if (SEAT_TYPE_PATTERNS.THREE_ROW_BUNDLE.some(pattern => lowerTag.includes(pattern))) {
      return 'THREE_ROW_BUNDLE';
    }
    if (SEAT_TYPE_PATTERNS.TWO_ROW_BUNDLE.some(pattern => lowerTag.includes(pattern))) {
      return 'TWO_ROW_BUNDLE';
    }
    if (SEAT_TYPE_PATTERNS.FIRST_ROW.some(pattern => lowerTag.includes(pattern))) {
      return 'FIRST_ROW';
    }
    if (SEAT_TYPE_PATTERNS.SECOND_THIRD_ROW.some(pattern => lowerTag.includes(pattern))) {
      return 'SECOND_THIRD_ROW';
    }
  }
  return null;
}

function detectSeatType(lineItem: any): string | null {
  if (lineItem.customAttributes && lineItem.customAttributes.length > 0) {
    const seatType = detectSeatTypeFromAttributes(lineItem.customAttributes);
    if (seatType) return seatType;
  }

  if (lineItem.product?.tags && lineItem.product.tags.length > 0) {
    const tagSeatType = detectSeatTypeFromTags(lineItem.product.tags);
    if (tagSeatType) return tagSeatType;
  }

  const titleSeatType = detectSeatTypeFromTitle(lineItem.title);
  if (titleSeatType) return titleSeatType;

  if (lineItem.variant?.title) {
    const variantSeatType = detectSeatTypeFromTitle(lineItem.variant.title);
    if (variantSeatType) return variantSeatType;
  }

  return null;
}

function isSeatProduct(lineItem: any): boolean {
  const commonSeatKeywords = [
    'seat', 'cover', 'luxury', 'custom', 'row', 'bundle', 'front', 'rear', 'headrest'
  ];

  const EXCLUDED_KEYWORDS = ['steering', 'wheel', 'console', 'armrest', 'sample', 'swatch'];

  const title = lineItem.title.toLowerCase();
  const variantTitle = lineItem.variant?.title?.toLowerCase() || '';

  // Check for excluded keywords first
  if (EXCLUDED_KEYWORDS.some(keyword => title.includes(keyword) || variantTitle.includes(keyword))) {
    return false;
  }

  const titleHasKeywords = commonSeatKeywords.some(keyword =>
    title.includes(keyword) || variantTitle.includes(keyword)
  );

  const hasCustomAttributes = lineItem.customAttributes?.some((attr: any) =>
    attr.key.toLowerCase().includes('seat') ||
    attr.key.toLowerCase().includes('row') ||
    attr.value.toLowerCase().includes('seat') ||
    attr.value.toLowerCase().includes('row')
  ) || false;

  return titleHasKeywords || hasCustomAttributes || detectSeatType(lineItem) !== null;
}

export function calculateSeatUploadRequirements(order: OrderDetails): SeatUploadRequirements | null {
  const seatRequirements: SeatRequirement[] = [];
  let totalPhotosRequired = 0;

  for (const edge of order.lineItems.edges) {
    const lineItem = edge.node;

    if (!isSeatProduct(lineItem)) {
      continue;
    }

    let seatType = detectSeatType(lineItem);

    console.log(`[Seat Detection] Item: "${lineItem.title}"`, {
      detectedType: seatType || 'FIRST_ROW (Fallback)',
      attributes: lineItem.customAttributes,
      tags: lineItem.product?.tags,
      variantTitle: lineItem.variant?.title,
      rawSeatType: seatType
    });

    if (!seatType) {
      seatType = 'FIRST_ROW'; // Default fallback
    }

    const config = SEAT_SLOT_CONFIG[seatType] || SEAT_SLOT_CONFIG['FIRST_ROW'];
    const photosRequired = config.length;

    for (let i = 0; i < lineItem.quantity; i++) {
      const itemId = lineItem.id + (i > 0 ? `-${i}` : '');

      // Generate slots with unique IDs for this item instance
      const slots: SeatSlot[] = config.map(slotConfig => ({
        ...slotConfig,
        id: `${itemId}_${slotConfig.imagePosition}`,
      }));

      seatRequirements.push({
        seatType,
        photosRequired,
        itemTitle: lineItem.title + (lineItem.variant?.title ? ` - ${lineItem.variant.title}` : ''),
        itemId,
        slots,
      });
      totalPhotosRequired += photosRequired;
    }
  }

  if (seatRequirements.length === 0) {
    return null;
  }

  // Generate instruction summary
  const seatTypeGroups = seatRequirements.reduce((groups, req) => {
    if (!groups[req.seatType]) {
      groups[req.seatType] = 0;
    }
    groups[req.seatType]++;
    return groups;
  }, {} as Record<string, number>);

  const instructionParts: string[] = [];
  Object.entries(seatTypeGroups).forEach(([seatType, count]) => {
    const config = SEAT_SLOT_CONFIG[seatType] || SEAT_SLOT_CONFIG['FIRST_ROW'];
    const photosPerItem = config.length;
    const totalForType = count * photosPerItem;
    const displayName = getSeatTypeDisplayName(seatType);

    const suffix = totalForType === 1 ? 'photo' : 'photos';
    let part = `${totalForType} ${suffix} for ${displayName}`;

    if (count > 1) {
      part += ` (${photosPerItem} per item)`;
    }

    instructionParts.push(part);
  });

  const instructionSummary = instructionParts.join(', ');

  return {
    totalPhotosRequired,
    seatRequirements,
    instructionSummary,
  };
}

export function getSeatTypeDisplayName(seatType: string): string {
  switch (seatType) {
    case 'FIRST_ROW':
      return 'First Row';
    case 'SECOND_THIRD_ROW':
      return 'Second or Third Row';
    case 'TWO_ROW_BUNDLE':
      return '2 Row Bundle';
    case 'THREE_ROW_BUNDLE':
      return '3 Row Bundle';
    default:
      return 'Unknown Seat Type';
  }
}